import { AiClientError } from '@/lib/ai/client';
import { requestPatch } from '@/lib/ai/patch';
import { useEditorStore } from '@/lib/state/editorStore';
import type { Fiche } from '@pharmafiche/schema';
import { useEffect, useRef, useState } from 'react';

interface AIPromptDrawerProps {
  open: boolean;
  onClose: () => void;
  fiche: Fiche;
  /** Called after a patch was applied; the shell uses this to fire the undo toast. */
  onPatchApplied: (info: { count: number; modelUsed: string; latencyMs: number }) => void;
}

export function AIPromptDrawer({ open, onClose, fiche, onPatchApplied }: AIPromptDrawerProps) {
  const focusedSectionId = useEditorStore((s) => s.focusedSectionId);
  const applyPatch = useEditorStore((s) => s.applyPatch);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus on open
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const focusedSection = focusedSectionId
    ? fiche.sections.find((s) => s.id === focusedSectionId)
    : null;
  const scopeLabel = focusedSection ? sectionLabel(focusedSection) : 'Toute la fiche';

  const submit = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const out = await requestPatch({
        fiche,
        prompt: trimmed,
        language: 'fr',
        ...(focusedSectionId ? { target_section_id: focusedSectionId } : {}),
      });
      if (out.patch.length === 0) {
        setError("Le modèle n'a rien à modifier. Reformule ta requête.");
        setBusy(false);
        return;
      }
      const result = applyPatch(out.patch);
      if (!result.ok) {
        setError(`Le patch n'a pas pu être appliqué : ${result.error}`);
        setBusy(false);
        return;
      }
      setPrompt('');
      setBusy(false);
      onPatchApplied({
        count: out.patch.length,
        modelUsed: out.model_used,
        latencyMs: out.latency_ms,
      });
      onClose();
    } catch (err) {
      const message = err instanceof AiClientError ? err.friendly : 'Erreur inconnue';
      setError(message);
      setBusy(false);
    }
  };

  return (
    <div
      className={[
        'fixed left-1/2 -translate-x-1/2 bottom-[80px] w-[min(680px,calc(100%-24px))] z-40 transition-transform duration-200 ease-out',
        open ? 'translate-y-0' : 'translate-y-[120%]',
      ].join(' ')}
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> can't be styled as a slide-up drawer this way
      role="dialog"
      aria-modal="false"
      aria-label="Demander à l'IA"
      aria-hidden={!open}
      data-testid="ai-drawer"
    >
      <div
        className="bg-white border-[2.5px] border-ink rounded-card p-3"
        style={{ boxShadow: '0 16px 40px rgba(0,0,0,.25), 4px 4px 0 #1f2330' }}
      >
        <div className="flex items-center justify-between mb-2">
          <span
            className={[
              'inline-flex items-center gap-2 px-3 py-1 border-2 border-ink rounded-full font-hand text-[13px]',
              focusedSection ? 'bg-accent-blue text-white' : 'bg-paper text-ink',
            ].join(' ')}
            data-testid="ai-scope"
          >
            <span aria-hidden>{focusedSection ? '🎯' : '🌐'}</span>
            <span>Édition : {scopeLabel}</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le panneau IA"
            className="text-ink hover:bg-ink/10 rounded-full w-7 h-7 grid place-items-center"
            disabled={busy}
          >
            ✕
          </button>
        </div>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Ex : Ajoute la posologie pédiatrique pour l'aspirine…"
            rows={2}
            disabled={busy}
            className="flex-1 px-3 py-2 border-2 border-ink rounded-control bg-paper font-hand text-[15px] resize-none focus:outline-none focus:ring-3 focus:ring-accent-blue/40 disabled:opacity-60"
            aria-label="Requête à l'IA"
            data-testid="ai-prompt"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || prompt.trim().length === 0}
            className="px-4 py-2 bg-accent-blue text-white font-hand text-[15px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40 disabled:cursor-not-allowed hover:translate-y-[-1px] transition-transform"
            data-testid="ai-send"
          >
            {busy ? '⏳' : 'Envoyer'}
          </button>
        </div>
        <p className="font-caveat text-ink-soft text-[14px] mt-2">
          {focusedSection
            ? 'Astuce : clique en dehors pour élargir à toute la fiche.'
            : 'Astuce : sélectionne une carte pour cibler la modification.'}
        </p>
        {error ? (
          <p className="font-hand text-warn-text text-[13px] mt-2" role="alert">
            ⚠️ {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function sectionLabel(
  section: { type: string; id: string } & Partial<{ title: string; drug: { name: string } }>,
): string {
  if (section.type === 'drug_card' && section.drug?.name) return `💊 ${section.drug.name}`;
  if ('title' in section && section.title) return section.title;
  if (section.type === 'mnemonic') return 'Mnémo';
  if (section.type === 'warning') return 'Avertissement';
  if (section.type === 'clinical_case') return 'Cas clinique';
  if (section.type === 'diagram') return 'Diagramme';
  return section.id;
}
